import type { Finding } from '#/graphql/types';
import {
  getAsset,
  getAssetList,
  getLocation,
  getLocationList,
  getUser,
  getUserList,
  getWorkOrder,
  getWorkOrderList,
} from '#/graphql/queries';
import { typeItems, statusItems, priorityItems } from './constants';
import { gql } from '@apollo/client';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';
import { color } from '@nuvolo/nuux-theme/utils';

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID',
    editorOptions: { readOnly: true },
  },
  displayNumber: {
    dataField: 'displayNumber',
    label: 'Number',
    editorOptions: { readOnly: true, placeholder: 'Auto generated' },
  },
  shortDescription: {
    dataField: 'shortDescription',
    label: 'Short Description',
    isRequired: true,
    editorType: 'TextArea',
    editorOptions: {
      maxLength: 1024, // Set max length to match backend validation
      validationRules: [
        {
          type: 'required',
          message: 'Short Description is required',
        },
      ],
    },
  },
  description: {
    dataField: 'description',
    label: 'Description',
    editorType: 'HtmlEditor',
    editorOptions: {
      height: 100,
      placeholder: 'Enter description',
      maxLength: 8192, // Set max length to match backend validation
    },
  },
  type: {
    dataField: 'type',
    label: 'Finding Type',
    editorType: 'SelectBox',
    isRequired: true,
    editorOptions: {
      items: typeItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  status: {
    dataField: 'status',
    label: 'Status',
    editorType: 'SelectBox',
    isRequired: true,
    editorOptions: {
      items: statusItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  priority: {
    dataField: 'priority',
    label: 'Priority',
    editorType: 'SelectBox',
    isRequired: false,
    editorOptions: {
      items: priorityItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  reportedOn: {
    dataField: 'reportedOn',
    label: 'Reported On',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      placeholder: 'Select date and time',
    },
  },
  assetId: {
    dataField: 'assetId',
    label: 'Asset',
    isRequired: true,
    editorType: 'RelatedItemsCards',
    editorOptions: {
      placeholder: 'Search assets...',
      dropdownColumns: 1,
      button: {
        tooltip: 'View asset details',
        url: '/assets/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'assetTag',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getAssetList),
        getItem: gql(getAsset),
        filter: ['state', 'notcontains', 'retired'],
      },
      cardConfig: {
        iconName: 'cube',
        iconColor: color('custom', 'constantTeal'),
        iconHolderOpacity: 0.2,
        subtitleExpr: 'model.name',
      },
    },
  },
  locationId: {
    dataField: 'locationId',
    label: 'Location',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search locations...',
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getLocationList),
        getItem: gql(getLocation),
      },
      button: {
        readOnly: true,
      },
    },
  },
  estimatedRepairCost: {
    dataField: 'estimatedRepairCost',
    label: 'Estimated Repair Cost',
    editorType: 'NumberBox',
    editorOptions: {
      format: 'currency',
    },
  },
  plannedRepairDate: {
    dataField: 'plannedRepairDate',
    label: 'Planned Repair Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
    },
  },
  cause: {
    dataField: 'cause',
    label: 'Cause',
    editorType: 'TextBox',
    editorOptions: {
      maxLength: 1024, // Set max length to match backend validation
    },
  },
  symptom: {
    dataField: 'symptom',
    label: 'Symptom',
    editorType: 'TextBox',
    editorOptions: {
      maxLength: 1024, // Set max length to match backend validation
    },
  },
  picture: {
    dataField: 'picture',
    label: 'Picture',
    editorType: 'Image',
    editorOptions: {
      height: '250px',
      alt: 'Selected image',
    },
    cssClass: 'hide-label',
  },
  workOrderId: {
    dataField: 'workOrderId',
    label: 'Work Order',
    editorType: 'RelatedItems',
    editorOptions: {
      readOnly: true,
      placeholder: 'Related work order',
      button: {
        tooltip: 'View work order details',
        url: '/work-orders/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'title',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getWorkOrderList),
        getItem: gql(getWorkOrder),
        filter: ['is_archived', '=', 'false'],
      },
    },
  },
  reportedById: {
    dataField: 'reportedById',
    label: 'Reported By',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search users...',
      button: {
        tooltip: 'View user details',
        url: '/users/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getUserList),
        getItem: gql(getUser),
      },
    },
  },
  model: {
    dataField: 'model.name',
    label: 'Model',
  },
} satisfies FieldDefs<Finding>;

export default fields;
